import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowUp,
  Brain,
  Check,
  ChevronDown,
  ChevronRight,
  Crosshair,
  GitPullRequest,
  Maximize2,
  Minimize2,
  Square,
  Trash2,
  X,
  XCircle,
} from 'lucide-react'
import { type PointerEvent as RPointerEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'wouter'
import { api, streamPost } from '../lib/api'
import { fmt } from '../lib/format'
import { keys } from '../lib/hooks'
import type { AgentEvent, AskSubject, Exhibit, ExplainNode, PageBlock } from '../lib/types'
import { CompactTree, explainToTree } from './DriverTree'
import { ExhibitCard, ExhibitPending } from './Exhibits'
import { Markdown } from './Markdown'
import { PinToPage } from './PinToPage'
import { useAgent, useWorkspace } from './Shell'
import { Spinner, useToast } from './ui'

type ToolItem = { kind: 'tool'; id: string; name: string; input: unknown; ok: boolean | null; summary: string; data?: unknown }
type ExhibitItem = {
  kind: 'exhibit'
  id: string
  name: string
  title: string
  input: unknown
  exhibit: Exhibit | null
  error: string | null
}
type Item =
  | { kind: 'user'; text: string }
  | { kind: 'text'; text: string }
  | { kind: 'thinking'; text: string }
  | ToolItem
  | ExhibitItem
  | { kind: 'changeset'; id: string }
  | { kind: 'error'; text: string }

const SUGGESTIONS = [
  'Summarize where the plan stands and what changed recently',
  'Which departments are furthest over budget, and why?',
  'Show how Data & AI opex is built for FY27',
  'Draft a scenario: freeze hiring in G&A for the rest of the year',
]

const SHOW: Record<string, Exhibit['kind']> = { show_table: 'table', show_chart: 'chart', show_driver_tree: 'tree' }

const STEP: Record<string, string> = {
  catalog_describe: 'Read the model',
  data_query: 'Queried',
  data_explain: 'Traced',
  model_read_file: 'Read',
  changeset_list: 'Listed changesets',
  changeset_open: 'Opened a changeset',
  changeset_transform: 'Staged an edit',
  changeset_write_values: 'Staged values',
  changeset_edit_model: 'Edited the model',
  changeset_validate: 'Validated',
  changeset_submit: 'Submitted',
}

function stepTitle(t: ToolItem): string {
  const a = (t.input ?? {}) as Record<string, unknown>
  const verb = STEP[t.name] ?? t.name
  if (t.name === 'data_query') return `${verb} ${a.measure}${Array.isArray(a.by) && a.by.length ? ` by ${a.by.join(', ')}` : ''}`
  if (t.name === 'data_explain') return `${verb} ${a.measure}`
  if (t.name === 'model_read_file') return `${verb} ${a.path}`
  return verb
}

/** A data_query result as a small table: the rows the agent looked at, labelled. */
function QueryPreview({ data }: { data: unknown }) {
  const d = data as {
    unit: string
    by: string[]
    rows: Record<string, string | number | null>[]
    row_count: number
    total: number | null
    labels?: Record<string, Record<string, string>>
  }
  if (!d?.rows) return null
  const format = d.unit === 'USD' ? 'usd' : d.unit === 'FTE' ? 'fte' : 'number'
  const rows = d.rows.slice(0, 12)
  return (
    <div className="x-table">
      <table>
        <thead>
          <tr>
            {d.by.map(l => (
              <th key={l}>{l.replace(/_/g, ' ')}</th>
            ))}
            <th className="r">value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="k-row">
              {d.by.map(l => (
                <td key={l} className="x-label">
                  {d.labels?.[l]?.[String(r[l])] ?? String(r[l])}
                </td>
              ))}
              <td className="r num">{fmt(r.value as number | null, format)}</td>
            </tr>
          ))}
          {d.row_count > rows.length && (
            <tr className="x-more">
              <td colSpan={d.by.length + 1}>{d.row_count - rows.length} more rows</td>
            </tr>
          )}
          {d.total !== null && d.by.length > 0 && (
            <tr className="k-total">
              <td className="x-label" colSpan={d.by.length}>
                Total
              </td>
              <td className="r num">{fmt(d.total, format)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function StepRow({ item }: { item: ToolItem }) {
  const [open, setOpen] = useState(false)
  const preview =
    item.ok && item.data ? (
      item.name === 'data_query' ? (
        <QueryPreview data={item.data} />
      ) : item.name === 'data_explain' ? (
        <CompactTree root={explainToTree(item.data as ExplainNode)} maxDepth={1} />
      ) : null
    ) : null
  const args = Object.entries((item.input ?? {}) as Record<string, unknown>).filter(([k]) => k !== 'rationale')
  return (
    <div className={`step ${item.ok === false ? 'err' : ''}`}>
      <button type="button" className="step-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="step-icon">
          {item.ok === null ? <Spinner size={11} /> : item.ok ? <Check size={11} /> : <XCircle size={11} />}
        </span>
        <span className="step-title">{stepTitle(item)}</span>
        <span className="step-sum">{item.summary}</span>
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
      </button>
      {open && (
        <div className="step-body">
          {preview}
          {args.length > 0 && (
            <div className="step-args">
              {args.map(([k, v]) => (
                <span key={k} className="step-arg">
                  <span className="faint">{k}</span> {typeof v === 'string' ? v : JSON.stringify(v)}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** The agent's working steps between answers, folded into one line once it's done. */
function Steps({ items, live }: { items: (ToolItem | Extract<Item, { kind: 'thinking' }>)[]; live: boolean }) {
  const [open, setOpen] = useState(live)
  useEffect(() => {
    if (!live) setOpen(false)
  }, [live])
  const tools = items.filter((i): i is ToolItem => i.kind === 'tool')
  const failed = tools.filter(t => t.ok === false).length
  return (
    <div className="steps">
      <button type="button" className="steps-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        {live ? <Spinner size={11} /> : <Brain size={12} />}
        <span>
          {live ? 'Working' : 'Worked through'} {tools.length} step{tools.length === 1 ? '' : 's'}
          {failed ? ` · ${failed} retried` : ''}
        </span>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {open && (
        <div className="steps-body">
          {items.map((it, i) =>
            it.kind === 'tool' ? (
              <StepRow key={it.id} item={it} />
            ) : (
              <div key={i} className="step-think">
                {it.text}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  )
}

const STORE = (wid: string) => `plan.agent.${wid}`
const WIDTH = 'plan.agent.width'
const EXPANDED = 'plan.agent.expanded'
const MIN_W = 360
const read = <T,>(k: string, d: T): T => {
  try {
    const v = localStorage.getItem(k)
    return v === null ? d : (JSON.parse(v) as T)
  } catch {
    return d
  }
}
const write = (k: string, v: unknown) => {
  try {
    localStorage.setItem(k, JSON.stringify(v))
  } catch {
    // private mode or storage blocked: the width just isn't remembered
  }
}
const maxWidth = () => Math.max(MIN_W, window.innerWidth - 236 - 480)

export function AgentPanel({ wid, onClose }: { wid: string; onClose: () => void }) {
  const [pin, setPin] = useState<{ block: PageBlock; what: string } | null>(null)
  const { pending, consume } = useAgent()
  const { me } = useWorkspace()
  const qc = useQueryClient()
  const toast = useToast()
  const [items, setItems] = useState<Item[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(STORE(wid)) ?? '[]') as Item[]
    } catch {
      return []
    }
  })
  const [input, setInput] = useState('')
  const [context, setContext] = useState<string | null>(null)
  // what the context chip points at, when it came from asking about something on screen
  const [subject, setSubject] = useState<AskSubject | null>(null)
  const [busy, setBusy] = useState(false)
  const [width, setWidth] = useState(() => read(WIDTH, 440))
  const [expanded, setExpanded] = useState(() => read(EXPANDED, false))
  const abort = useRef<AbortController | null>(null)
  const scroll = useRef<HTMLDivElement>(null)
  const resizing = useRef<{ x: number; w: number } | null>(null)

  // the panel's width drives the app grid; expanded overlays the page (which keeps its layout underneath)
  useEffect(() => {
    const apply = () => {
      const w = Math.min(width, maxWidth())
      document.documentElement.style.setProperty('--agent-w', `${Math.round(w)}px`)
    }
    apply()
    window.addEventListener('resize', apply)
    return () => window.removeEventListener('resize', apply)
  }, [width])
  useEffect(
    () => () => {
      document.documentElement.style.removeProperty('--agent-w')
    },
    [],
  )
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'j') {
        e.preventDefault()
        setExpanded(x => {
          write(EXPANDED, !x)
          return !x
        })
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [])

  useEffect(() => {
    try {
      sessionStorage.setItem(STORE(wid), JSON.stringify(items.slice(-80)))
    } catch {
      // storage full or unavailable: the conversation just isn't persisted
    }
  }, [items, wid])
  // biome-ignore lint/correctness/useExhaustiveDependencies: follow the newest message whenever the conversation changes
  useEffect(() => {
    const el = scroll.current
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 320) el.scrollTo({ top: el.scrollHeight })
  }, [items])

  const send = useCallback(
    async (prompt: string, ctx: string | null, subj: AskSubject | null = null) => {
      if (!prompt.trim() || busy) return
      const history = items
        .filter((i): i is Extract<Item, { kind: 'user' | 'text' }> => i.kind === 'user' || i.kind === 'text')
        .map(i => ({ role: i.kind === 'user' ? ('user' as const) : ('assistant' as const), content: i.text }))
        .filter(h => h.content.trim())
        .slice(-20)
      setItems(it => [...it, { kind: 'user', text: prompt }])
      setInput('')
      setBusy(true)
      requestAnimationFrame(() => scroll.current?.scrollTo({ top: scroll.current.scrollHeight }))
      const ac = new AbortController()
      abort.current = ac
      const on = (e: AgentEvent) => {
        switch (e.type) {
          case 'start':
          case 'done':
          case 'message':
            break
          case 'text':
            setItems(it => {
              const last = it.at(-1)
              if (last?.kind === 'text') return [...it.slice(0, -1), { kind: 'text', text: last.text + e.delta }]
              return [...it, { kind: 'text', text: e.delta }]
            })
            break
          case 'thinking':
            setItems(it => [...it, { kind: 'thinking', text: e.text }])
            break
          case 'tool_call': {
            const kind = SHOW[e.name]
            const title = String((e.input as { title?: unknown })?.title ?? 'Exhibit')
            setItems(it => [
              ...it,
              kind
                ? { kind: 'exhibit', id: e.id, name: e.name, title, input: e.input, exhibit: null, error: null }
                : { kind: 'tool', id: e.id, name: e.name, input: e.input, ok: null, summary: 'running…' },
            ])
            break
          }
          case 'tool_result':
            setItems(it =>
              it.map(x => {
                if (x.kind === 'exhibit' && x.id === e.id)
                  return e.ok
                    ? { ...x, exhibit: e.data as Exhibit }
                    : { ...x, error: (e.data as { message?: string })?.message ?? e.summary }
                if (x.kind === 'tool' && x.id === e.id) {
                  // keep query and trace results small enough to replay from session storage
                  const data =
                    e.ok && e.name === 'data_query'
                      ? { ...(e.data as object), rows: ((e.data as { rows?: unknown[] }).rows ?? []).slice(0, 12) }
                      : e.ok && e.name === 'data_explain'
                        ? e.data
                        : undefined
                  return { ...x, ok: e.ok, summary: e.summary, data }
                }
                return x
              }),
            )
            // a page the agent made or changed: refresh the list, and link to it
            if (e.ok && e.name.startsWith('page_')) {
              void qc.invalidateQueries({ queryKey: ['ws', wid, 'pages'] })
              void qc.invalidateQueries({ queryKey: ['ws', wid, 'page'] })
              const made = e.data as { id?: string; title?: string } | undefined
              if (e.name === 'page_create' && made?.id)
                setItems(it => [...it, { kind: 'text', text: `**[Open “${made.title ?? 'the page'}” →](/w/${wid}/pages/${made.id})**` }])
            }
            break
          case 'changeset':
            setItems(it => (it.some(x => x.kind === 'changeset' && x.id === e.id) ? it : [...it, { kind: 'changeset', id: e.id }]))
            void qc.invalidateQueries({ queryKey: keys.ws(wid) })
            break
          case 'error':
            setItems(it => [...it, { kind: 'error', text: e.message }])
            break
          default: {
            const never: never = e
            return never
          }
        }
      }
      try {
        await streamPost<AgentEvent>(`/w/${wid}/agent`, { prompt, history, context: ctx, subject: subj }, on, ac.signal)
      } catch (err) {
        if (!ac.signal.aborted) setItems(it => [...it, { kind: 'error', text: (err as Error).message }])
      } finally {
        setBusy(false)
        abort.current = null
        // anything still marked running was cut off
        setItems(it => it.map(x => (x.kind === 'tool' && x.ok === null ? { ...x, ok: false, summary: 'stopped' } : x)))
        void qc.invalidateQueries({ queryKey: keys.ws(wid) })
      }
    },
    [busy, items, qc, wid],
  )

  useEffect(() => {
    if (!pending) return
    consume()
    setContext(pending.context)
    setSubject(pending.subject)
    void send(pending.prompt, pending.context, pending.subject)
  }, [pending, consume, send])

  const onResizeDown = (e: RPointerEvent) => {
    resizing.current = { x: e.clientX, w: expanded ? maxWidth() : width }
    ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
    document.body.classList.add('resizing')
  }
  const onResizeMove = (e: RPointerEvent) => {
    const r = resizing.current
    if (!r) return
    const w = Math.max(MIN_W, Math.min(maxWidth(), r.w + (r.x - e.clientX)))
    setExpanded(false)
    setWidth(w)
  }
  const onResizeUp = () => {
    if (!resizing.current) return
    resizing.current = null
    document.body.classList.remove('resizing')
    write(WIDTH, width)
    write(EXPANDED, false)
  }
  const toggleExpanded = () => {
    setExpanded(x => {
      write(EXPANDED, !x)
      return !x
    })
  }

  const available = me?.agent.available ?? false

  // consecutive working steps render as one disclosure
  const rendered: React.ReactNode[] = []
  let run: (ToolItem | Extract<Item, { kind: 'thinking' }>)[] = []
  const flush = (key: string, live: boolean) => {
    if (run.length && run.some(r => r.kind === 'tool')) rendered.push(<Steps key={key} items={run} live={live} />)
    else
      for (const [j, r] of run.entries())
        if (r.kind === 'thinking')
          rendered.push(
            <div key={`${key}-t${j}`} className="msg-think">
              <Brain size={13} style={{ flex: 'none', marginTop: 2 }} />
              <span>{r.text}</span>
            </div>,
          )
    run = []
  }
  for (const [i, it] of items.entries()) {
    if (it.kind === 'tool' || it.kind === 'thinking') {
      run.push(it)
      continue
    }
    flush(`s${i}`, false)
    switch (it.kind) {
      case 'user':
        rendered.push(
          <div key={i} className="msg-user">
            {it.text}
          </div>,
        )
        break
      case 'text':
        if (it.text.trim())
          rendered.push(
            <div key={i} className="msg-agent">
              <Markdown text={it.text} />
            </div>,
          )
        break
      case 'exhibit':
        rendered.push(
          it.exhibit ? (
            <ExhibitCard
              key={i}
              e={it.exhibit}
              onPin={
                it.input
                  ? () =>
                      setPin({
                        block: {
                          type: SHOW[it.name] ?? 'table',
                          input: { ...(it.input as Record<string, unknown>), title: it.exhibit?.title },
                        } as PageBlock,
                        what: `“${it.exhibit?.title ?? 'This answer'}”`,
                      })
                  : undefined
              }
              onRefresh={
                it.input
                  ? async () => {
                      try {
                        const fresh = await api.post<Exhibit>(`/w/${wid}/exhibits`, { tool: it.name, input: it.input })
                        setItems(xs => xs.map(x => (x.kind === 'exhibit' && x.id === it.id ? { ...x, exhibit: fresh } : x)))
                      } catch (err) {
                        toast('Could not refresh', (err as Error).message)
                      }
                    }
                  : undefined
              }
            />
          ) : it.error ? (
            <div key={i} className="issue small">
              Could not build “{it.title}”: {it.error}
            </div>
          ) : (
            <ExhibitPending key={i} title={it.title} kind={SHOW[it.name] ?? 'table'} />
          ),
        )
        break
      case 'changeset':
        rendered.push(
          <Link key={i} href={`/changesets/${it.id}`} className="notice row" style={{ textDecoration: 'none', color: 'inherit' }}>
            <GitPullRequest size={14} /> Proposed a change — review it
          </Link>,
        )
        break
      case 'error':
        rendered.push(
          <div key={i} className="issue">
            {it.text}
          </div>,
        )
        break
      default: {
        const never: never = it
        void never
      }
    }
  }
  flush('s-end', busy)

  return (
    <aside className={`agent-panel ${expanded ? 'expanded' : ''}`} aria-label="Planning agent">
      {pin && <PinToPage block={pin.block} what={pin.what} onClose={() => setPin(null)} />}
      {/* biome-ignore lint/a11y/useSemanticElements: a focusable splitter (drag or arrow keys) is the ARIA separator widget, which <hr> can't be */}
      <div
        className="agent-resize"
        role="separator"
        tabIndex={0}
        aria-orientation="vertical"
        aria-valuemin={MIN_W}
        aria-valuemax={maxWidth()}
        aria-valuenow={Math.round(expanded ? maxWidth() : width)}
        aria-label="Resize the agent panel"
        title="Drag to resize · double-click to reset"
        onKeyDown={e => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
          e.preventDefault()
          const w = Math.max(MIN_W, Math.min(maxWidth(), (expanded ? maxWidth() : width) + (e.key === 'ArrowLeft' ? 40 : -40)))
          setExpanded(false)
          setWidth(w)
          write(WIDTH, w)
          write(EXPANDED, false)
        }}
        onPointerDown={onResizeDown}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
        onDoubleClick={() => {
          setExpanded(false)
          setWidth(440)
          write(WIDTH, 440)
          write(EXPANDED, false)
        }}
      />
      <div className="agent-head">
        <strong style={{ fontWeight: 550 }}>Planning agent</strong>
        <span className="faint tiny mono">{me?.agent.model}</span>
        <div className="grow" />
        {items.length > 0 && (
          <button type="button" className="btn ghost sm" title="Clear conversation" onClick={() => setItems([])} disabled={busy}>
            <Trash2 size={13} />
          </button>
        )}
        <button
          type="button"
          className="btn ghost sm"
          onClick={toggleExpanded}
          aria-label={expanded ? 'Collapse the agent panel' : 'Expand the agent panel'}
          title={expanded ? 'Collapse (⌘⇧J)' : 'Expand (⌘⇧J)'}
        >
          {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        </button>
        <button type="button" className="btn ghost sm" onClick={onClose} aria-label="Close agent">
          <X size={15} />
        </button>
      </div>
      <div className="agent-scroll" ref={scroll}>
        <div className="agent-thread">
          {!available && (
            <div className="notice">
              The agent is not configured on this deployment. Set <code>ANTHROPIC_API_KEY</code> on the server to enable it. Everything else
              works without it, and external agents can still connect over MCP (Settings → API tokens).
            </div>
          )}
          {items.length === 0 && available && (
            <div style={{ display: 'grid', gap: 10 }}>
              <div className="muted small">
                The agent reads the model and the plan, shows its numbers as tables, charts and driver trees straight from the engine, and
                proposes changes as changesets you review. It can’t merge anything you couldn’t.
              </div>
              <div className="chipbar" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                {SUGGESTIONS.map(s => (
                  <button type="button" key={s} className="chip" onClick={() => void send(s, context, subject)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {rendered}
          {busy && run.length === 0 && items.at(-1)?.kind !== 'tool' && (
            <div className="msg-think">
              <Spinner size={12} /> thinking…
            </div>
          )}
        </div>
      </div>
      <form
        className="agent-input"
        onSubmit={e => {
          e.preventDefault()
          void send(input, context, subject)
        }}
      >
        {context && (
          <div className="agent-context">
            <Crosshair size={11} />{' '}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{context}</span>
            <button
              type="button"
              className="btn ghost sm"
              style={{ padding: '0 4px' }}
              onClick={() => {
                setContext(null)
                setSubject(null)
              }}
            >
              <X size={11} />
            </button>
          </div>
        )}
        <textarea
          className="input"
          value={input}
          disabled={!available}
          placeholder={available ? 'Ask about the plan or ask for a change…' : 'Agent unavailable'}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault()
              void send(input, context, subject)
            }
          }}
        />
        <div className="row">
          <span className="faint tiny">↵ send · ⇧↵ newline · ⌘J toggle · ⌘⇧J expand</span>
          <div className="grow" />
          {busy ? (
            <button type="button" className="btn sm" onClick={() => abort.current?.abort()}>
              <Square size={12} /> Stop
            </button>
          ) : (
            <button type="submit" className="btn primary sm" disabled={!input.trim() || !available}>
              <ArrowUp size={13} /> Send
            </button>
          )}
        </div>
      </form>
    </aside>
  )
}
